import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AssignmentOutlined as AssignmentIcon,
  CallEnd as CallEndIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  GraphicEq as GraphicEqIcon,
  LightbulbOutlined as LightbulbIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
  Replay as ReplayIcon,
  Subject as TranscriptIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { API_URL } from '../config';
import { LiveStt } from '../lib/liveStt';
import { useLanguage } from '../contexts/LanguageContext';
import usePracticeLocalization from '../hooks/usePracticeLocalization';
import { ENGLISH_SCENARIOS, localizeScenarioTitle } from '../i18n/scenarios';


const timestamp = () => new Date().toISOString();

const makeMessage = (role, text) => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  role,
  text,
  timestamp: timestamp(),
});

const formatTime = (seconds) => (
  `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`
);

const countCheckpoints = (nodes) => {
  const checkpoints = (nodes || []).flatMap((group) => (
    (group.nodes || []).flatMap((node) => node.checkpoint_status || [])
  ));
  const completed = checkpoints.filter((checkpoint) => checkpoint.completed).length;
  return { completed, incomplete: Math.max(0, checkpoints.length - completed) };
};

function Waveform({ active, color = '#1976d2' }) {
  const [levels, setLevels] = useState(() => Array(24).fill(0.12));

  useEffect(() => {
    const interval = setInterval(() => {
      setLevels((previous) => previous.map((_, index) => {
        if (!active) return 0.1;
        const center = 1 - Math.abs(index - 12) / 18;
        return Math.max(0.08, (0.22 + Math.random() * 0.78) * center);
      }));
    }, 110);
    return () => clearInterval(interval);
  }, [active]);

  return (
    <Box sx={{ height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
      {levels.map((level, index) => (
        <Box
          key={index}
          sx={{
            width: 3,
            height: Math.max(3, level * 34),
            borderRadius: 3,
            bgcolor: color,
            opacity: 0.3 + level * 0.7,
            transition: 'height 110ms linear, opacity 110ms linear',
          }}
        />
      ))}
    </Box>
  );
}

function CallControl({ label, children, onClick, disabled, primary, danger }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75 }}>
      <IconButton
        onClick={onClick}
        disabled={disabled}
        sx={{
          width: primary ? 72 : 56,
          height: primary ? 72 : 56,
          bgcolor: danger ? '#d32f2f' : primary ? '#16855b' : '#fff',
          color: danger || primary ? '#fff' : '#1f2937',
          boxShadow: '0 8px 28px rgba(15, 23, 42, 0.12)',
          border: danger || primary ? 'none' : '1px solid rgba(15,23,42,0.08)',
          '&:hover': { bgcolor: danger ? '#b71c1c' : primary ? '#106b49' : '#f5f7fb' },
          '&.Mui-disabled': { bgcolor: '#e5e7eb', color: '#9ca3af' },
        }}
      >
        {children}
      </IconButton>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Box>
  );
}

function PracticeSummary({ scenario, title, translateContent, elapsed, tasksDone, messages, analysis, nodes, loading, error, onRetry, onExit }) {
  const { tr } = useLanguage();
  const score = Math.round(analysis?.scores?.total || 0);
  const completion = Math.round(analysis?.completion_rate || 0);
  const checkpointCounts = countCheckpoints(nodes);

  return (
    <Box sx={{ position: 'fixed', inset: 0, zIndex: 1500, overflowY: 'auto', bgcolor: '#f4f7fb' }}>
      <Box sx={{ maxWidth: 980, mx: 'auto', px: { xs: 2, md: 4 }, py: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box>
            <Typography variant="overline" color="primary" fontWeight={800}>{tr('實戰演練完成', 'Practice complete')}</Typography>
            <Typography variant="h4" fontWeight={800}>{title}</Typography>
          </Box>
          <IconButton onClick={onExit} aria-label={tr('關閉', 'Close')}><CloseIcon /></IconButton>
        </Box>

        {loading ? (
          <Paper sx={{ p: 8, borderRadius: 4, textAlign: 'center' }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }} fontWeight={700}>{tr('Sonnet 5 正在產生講評…', 'Sonnet 5 is writing your review…')}</Typography>
          </Paper>
        ) : (
          <>
            {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.3fr 1fr' }, gap: 2 }}>
              <Paper sx={{ p: 3, borderRadius: 4, color: '#fff', background: 'linear-gradient(135deg,#123d78,#1976d2)' }}>
                <Typography variant="body2" sx={{ opacity: 0.82 }}>{tr('總體表現', 'Overall performance')}</Typography>
                <Typography variant="h2" fontWeight={900}>{score}</Typography>
                <Typography variant="body2" sx={{ mt: 2, opacity: 0.9 }}>{tr('對話完成度', 'Conversation completion')} {completion}%</Typography>
                <LinearProgress
                  variant="determinate"
                  value={completion}
                  sx={{ mt: 1, height: 9, borderRadius: 8, bgcolor: 'rgba(255,255,255,.22)', '& .MuiLinearProgress-bar': { bgcolor: '#fff' } }}
                />
              </Paper>
              <Paper sx={{ p: 3, borderRadius: 4 }}>
                <Typography variant="h6" fontWeight={800}>{tr('本次紀錄', 'This session')}</Typography>
                <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Box><Typography variant="h5" fontWeight={800}>{formatTime(elapsed)}</Typography><Typography variant="caption">{tr('練習時間', 'Practice time')}</Typography></Box>
                  <Box><Typography variant="h5" fontWeight={800}>{messages.filter((item) => item.role === 'advisor').length}</Typography><Typography variant="caption">{tr('專員發言', 'Advisor turns')}</Typography></Box>
                  <Box><Typography variant="h5" fontWeight={800}>{tasksDone}/{scenario.tasks.length}</Typography><Typography variant="caption">{tr('即時任務', 'Live tasks')}</Typography></Box>
                  <Box><Typography variant="h5" fontWeight={800}>{checkpointCounts.completed}</Typography><Typography variant="caption">{tr('達成查核點', 'Checkpoints met')}</Typography></Box>
                </Box>
              </Paper>
            </Box>

            <Paper sx={{ mt: 2, p: 3, borderRadius: 4 }}>
              <Typography variant="h6" fontWeight={800}>{tr('整體講評', 'Overall review')}</Typography>
              <Typography sx={{ mt: 1.5, lineHeight: 1.8 }}>{analysis?.overall || tr('講評暫時無法產生。', 'The review is not available right now.')}</Typography>
            </Paper>

            {analysis?.suggestions?.overall && (
              <Paper sx={{ mt: 2, p: 3, borderRadius: 4 }}>
                <Typography variant="h6" fontWeight={800}>{tr('改善建議', 'Recommendations')}</Typography>
                <Typography sx={{ mt: 1.5, lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>{analysis.suggestions.overall}</Typography>
                {Object.entries(analysis.suggestions.stages || {}).map(([stage, suggestion]) => (
                  <Box key={stage} sx={{ mt: 2, pl: 2, borderLeft: '3px solid #1976d2' }}>
                    <Typography fontWeight={800}>{translateContent(stage)}</Typography>
                    <Typography color="text.secondary">{suggestion}</Typography>
                  </Box>
                ))}
              </Paper>
            )}

            {(analysis?.common_mistakes || []).length > 0 && (
              <Paper sx={{ mt: 2, p: 3, borderRadius: 4 }}>
                <Typography variant="h6" fontWeight={800}>{tr('常見錯誤提醒', 'Common mistakes')}</Typography>
                {analysis.common_mistakes.map((mistake, index) => (
                  <Alert key={index} severity={mistake.severity === 'high' ? 'error' : 'warning'} sx={{ mt: 1.5 }}>
                    {translateContent(mistake.text)}{mistake.original_sentence ? `${tr('：', ': ')}${mistake.original_sentence}` : ''}
                  </Alert>
                ))}
              </Paper>
            )}

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 2 }}>
              <Button variant="outlined" size="large" startIcon={<ReplayIcon />} onClick={onRetry}>{tr('再練一次', 'Practice again')}</Button>
              <Button variant="contained" size="large" onClick={onExit}>{tr('返回情境', 'Back to scenarios')}</Button>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}

export default function LivePractice({ scenario, onExit, onRetry }) {
  const { tr, t, isEnglish, language } = useLanguage();
  // English UI: the whole call runs in English (customer, hints, voice, speech recognition, review)
  const { translateContent } = usePracticeLocalization(scenario.id);
  const openingLine = isEnglish ? (scenario.opening_line_en || scenario.opening_line) : scenario.opening_line;
  const displayName = isEnglish ? (ENGLISH_SCENARIOS[scenario.id]?.name || translateContent(scenario.name)) : scenario.name;
  const displayTitle = localizeScenarioTitle(scenario.title, scenario.id, isEnglish);
  const [messages, setMessages] = useState(() => [makeMessage('customer', openingLine)]);
  const [status, setStatus] = useState('connecting');
  const [elapsed, setElapsed] = useState(0);
  const [partial, setPartial] = useState('');
  const [typing, setTyping] = useState(false);
  const [aiSpeaking, setAiSpeaking] = useState(false);
  const [paused, setPaused] = useState(false);
  const [micMode, setMicMode] = useState('open');
  const [holding, setHolding] = useState(false);
  const [hint, setHint] = useState('');
  const [hintLoading, setHintLoading] = useState(false);
  const [completedTasks, setCompletedTasks] = useState(new Set());
  const [showTasks, setShowTasks] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [analysisNodes, setAnalysisNodes] = useState([]);
  const [summaryError, setSummaryError] = useState('');

  const engineRef = useRef(null);
  const audioRef = useRef(null);
  const audioUrlRef = useRef('');
  const messagesRef = useRef(messages);
  const typingRef = useRef(false);
  const pausedRef = useRef(false);
  const micModeRef = useRef('open');
  const speechStartedRef = useRef(0);
  const startedAtRef = useRef(Date.now());
  const conversationIdRef = useRef(Date.now());
  const sendUtteranceRef = useRef(() => {});
  const playSpeechRef = useRef(() => Promise.resolve());
  const finishRef = useRef(() => {});

  useEffect(() => { messagesRef.current = messages; }, [messages]);

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
      audioRef.current = null;
    }
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = '';
    }
    window.speechSynthesis?.cancel();
    setAiSpeaking(false);
  };

  const playSpeech = async (text, afterPlayback) => {
    stopAudio();
    try {
      const response = await fetch(`${API_URL}/api/live-practice/tts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scenario_id: scenario.id, text, language }),
      });
      if (!response.ok) throw new Error('tts unavailable');
      const url = URL.createObjectURL(await response.blob());
      audioUrlRef.current = url;
      const audio = new Audio(url);
      audioRef.current = audio;
      speechStartedRef.current = Date.now();
      setAiSpeaking(true);
      await new Promise((resolve) => {
        const finish = () => {
          setAiSpeaking(false);
          if (audioUrlRef.current === url) {
            URL.revokeObjectURL(url);
            audioUrlRef.current = '';
          }
          resolve();
        };
        audio.onended = finish;
        audio.onerror = finish;
        audio.onpause = finish;
        audio.play().catch(finish);
      });
    } catch (speechError) {
      if ('speechSynthesis' in window) {
        await new Promise((resolve) => {
          const utterance = new SpeechSynthesisUtterance(text);
          utterance.lang = isEnglish ? 'en-US' : 'zh-TW';
          utterance.rate = 0.95;
          utterance.onend = resolve;
          utterance.onerror = resolve;
          setAiSpeaking(true);
          window.speechSynthesis.speak(utterance);
        });
        setAiSpeaking(false);
      }
    }
    afterPlayback?.();
  };
  playSpeechRef.current = playSpeech;

  const appendMessages = (nextMessages) => {
    messagesRef.current = nextMessages;
    setMessages(nextMessages);
  };

  const sendUtterance = async (text) => {
    const cleaned = text.trim();
    if (!cleaned || typingRef.current || summary) return;
    typingRef.current = true;
    setTyping(true);
    setPartial('');
    engineRef.current?.setPaused(true);
    const withAdvisor = [...messagesRef.current, makeMessage('advisor', cleaned)];
    appendMessages(withAdvisor);

    try {
      const response = await axios.post(`${API_URL}/api/live-practice/respond`, {
        scenario_id: scenario.id,
        messages: withAdvisor.map(({ role, text: messageText }) => ({ role, text: messageText })),
        language,
      });
      const customerMessage = makeMessage('customer', response.data.customer_response);
      appendMessages([...withAdvisor, customerMessage]);
      setHint('');
      setCompletedTasks((previous) => {
        const next = new Set(previous);
        (response.data.completed_tasks || []).forEach((task) => next.add(task));
        return next;
      });
      typingRef.current = false;
      setTyping(false);
      if (!pausedRef.current && micModeRef.current === 'open') {
        engineRef.current?.setPaused(false);
      }
      await playSpeechRef.current(response.data.customer_response, () => {
        if (response.data.should_end) {
          setTimeout(() => finishRef.current(), 500);
        }
      });
    } catch (requestError) {
      setError(requestError.response?.data?.detail || tr('客戶回應暫時無法產生，請再說一次。', "The customer's reply couldn't be generated. Please say that again."));
    } finally {
      typingRef.current = false;
      setTyping(false);
      if (!pausedRef.current && micModeRef.current === 'open') {
        engineRef.current?.setPaused(false);
      }
    }
  };
  sendUtteranceRef.current = sendUtterance;

  useEffect(() => {
    const timer = setInterval(() => setElapsed(Math.floor((Date.now() - startedAtRef.current) / 1000)), 1000);
    const engine = new LiveStt({
      languageHints: isEnglish ? ['en'] : ['zh'],
      onReady: () => {
        setStatus('connected');
        playSpeechRef.current(openingLine);
      },
      onUtterance: (text) => sendUtteranceRef.current(text),
      onPartial: setPartial,
      onSpeaking: () => {
        if (audioRef.current && !audioRef.current.paused && Date.now() - speechStartedRef.current > 500) {
          audioRef.current.pause();
          setAiSpeaking(false);
        }
      },
      onError: (message) => {
        setError(message);
        setStatus('error');
      },
    });
    engineRef.current = engine;
    engine.start().catch((startError) => {
      setStatus('error');
      setError(startError.message || tr('無法啟動麥克風，請確認瀏覽器權限。', 'Could not start the microphone. Please check your browser permissions.'));
    });

    return () => {
      clearInterval(timer);
      engine.stop();
      engineRef.current = null;
      stopAudio();
    };
    // Start a fresh live engine only when the selected scenario changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenario.id]);

  const fetchHint = async () => {
    if (hintLoading) return;
    setHintLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/live-practice/hint`, {
        scenario_id: scenario.id,
        messages: messagesRef.current.map(({ role, text }) => ({ role, text })),
        language,
      });
      setHint(response.data.hint || '');
    } catch (hintError) {
      setError(tr('暫時無法取得建議提示。', "Couldn't get a hint right now."));
    } finally {
      setHintLoading(false);
    }
  };

  const finishPractice = async () => {
    if (summary || summaryLoading) return;
    const finalMessages = messagesRef.current;
    const practiceTime = Math.max(1, Math.floor((Date.now() - startedAtRef.current) / 1000));
    engineRef.current?.stop();
    engineRef.current = null;
    stopAudio();
    setStatus('ended');
    setSummary(true);
    setSummaryLoading(true);

    const nodeHistory = finalMessages.map((message) => ({
      role: message.role === 'advisor' ? 'user' : 'assistant',
      content: message.text,
    }));
    const analysisHistory = finalMessages.map((message) => ({
      text: message.text,
      sender: message.role === 'advisor' ? 'user' : 'bot',
      timestamp: message.timestamp,
    }));

    try {
      const nodeResponse = await axios.post(`${API_URL}/api/get_node_completion_status`, {
        scenario_id: scenario.id,
        prompt: '',
        nodes: [],
        history: nodeHistory,
        conversation_id: conversationIdRef.current,
      });
      const nodes = nodeResponse.data.nodes || [];
      setAnalysisNodes(nodes);
      const analysisResponse = await axios.post(`${API_URL}/api/analysis`, {
        scenario_id: scenario.id,
        history: analysisHistory,
        nodes,
        conversation_id: conversationIdRef.current,
        language,
      });
      const parsedAnalysis = typeof analysisResponse.data.analysis === 'string'
        ? JSON.parse(analysisResponse.data.analysis)
        : analysisResponse.data.analysis;
      setAnalysis(parsedAnalysis);

      try {
        const storedUser = JSON.parse(localStorage.getItem('user') || 'null');
        const checkpointCounts = countCheckpoints(nodes);
        await axios.put(`${API_URL}/api/storage/conversations/${conversationIdRef.current}`, {
          member_id: storedUser?.id || null,
          scenario_id: scenario.id,
          scenario_title: `[LIVE] ${scenario.title}`,
          timestamp: timestamp(),
          messages: analysisHistory,
        });
        await axios.put(`${API_URL}/api/storage/conversations/${conversationIdRef.current}/analysis`, {
          member_id: storedUser?.id || null,
          analysis_data: parsedAnalysis,
          total_score: Math.round(parsedAnalysis?.scores?.total || 0),
          completed_checkpoints: checkpointCounts.completed,
          incomplete_checkpoints: checkpointCounts.incomplete,
          completion_rate: Math.round(parsedAnalysis?.completion_rate || 0),
          practice_time: practiceTime,
          nodes_data: nodes,
        });
      } catch (storageError) {
        console.error('Unable to save live practice history', storageError);
      }
    } catch (analysisError) {
      setSummaryError(analysisError.response?.data?.detail || analysisError.response?.data?.error || tr('講評產生失敗，但逐字稿仍保留在本頁。', 'The review could not be generated, but the transcript is still on this page.'));
    } finally {
      setSummaryLoading(false);
    }
  };
  finishRef.current = finishPractice;

  const togglePause = () => {
    const next = !pausedRef.current;
    pausedRef.current = next;
    setPaused(next);
    engineRef.current?.setPaused(next || micModeRef.current === 'hold');
    if (next) stopAudio();
  };

  const toggleMicMode = () => {
    const next = micModeRef.current === 'open' ? 'hold' : 'open';
    micModeRef.current = next;
    setMicMode(next);
    if (engineRef.current) {
      engineRef.current.manual = next === 'hold';
      engineRef.current.setPaused(next === 'hold' || pausedRef.current);
    }
  };

  const holdStart = () => {
    if (pausedRef.current || status !== 'connected') return;
    setHolding(true);
    engineRef.current?.setPaused(false);
  };

  const holdEnd = () => {
    if (!holding) return;
    setHolding(false);
    engineRef.current?.finalize();
    setTimeout(() => engineRef.current?.setPaused(true), 750);
  };

  if (summary) {
    return (
      <PracticeSummary
        scenario={scenario}
        title={displayTitle}
        translateContent={translateContent}
        elapsed={elapsed}
        tasksDone={completedTasks.size}
        messages={messages}
        analysis={analysis}
        nodes={analysisNodes}
        loading={summaryLoading}
        error={summaryError}
        onRetry={onRetry}
        onExit={onExit}
      />
    );
  }

  const lastCustomer = [...messages].reverse().find((message) => message.role === 'customer');
  const listening = status === 'connected' && !paused && !typing && !aiSpeaking && (micMode === 'open' || holding);
  const statusTitle = status === 'connecting'
    ? tr('正在建立即時語音連線…', 'Connecting live audio…')
    : status === 'error'
      ? tr('即時語音暫時無法使用', 'Live audio is unavailable right now')
      : paused
        ? tr('麥克風已靜音', 'Microphone muted')
        : aiSpeaking
          ? tr(`${displayName}正在說話`, `${displayName} is speaking`)
          : typing
            ? tr(`${displayName}正在思考`, `${displayName} is thinking`)
            : micMode === 'hold' && !holding
              ? tr('按住麥克風開始說話', 'Hold the microphone to speak')
              : tr('換你說話', 'Your turn to speak');

  return (
    <Box sx={{ position: 'fixed', inset: 0, zIndex: 1500, bgcolor: '#f6f8fc', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box component="header" sx={{ flex: 'none', px: { xs: 2, md: 4 }, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid rgba(15,23,42,.08)', bgcolor: '#fff' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: status === 'connected' ? '#16a36a' : status === 'error' ? '#d32f2f' : '#f5a623' }} />
          <Typography variant="body2" color="text.secondary">{tr('LIVE 實戰通話', 'LIVE practice call')}</Typography>
        </Box>
        <Typography fontWeight={800} sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatTime(elapsed)}</Typography>
        <Chip label={`${t(`category.${scenario.category}`)} · ${isEnglish ? (scenario.difficulty_en || scenario.difficulty) : scenario.difficulty}`} size="small" />
      </Box>

      <Box sx={{ minHeight: 0, flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', px: 2, overflowY: 'auto' }}>
        <Box sx={{ pt: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Box sx={{ position: 'relative', width: { xs: 104, md: 128 }, height: { xs: 104, md: 128 } }}>
            {(aiSpeaking || listening) && (
              <Box sx={{ position: 'absolute', inset: -10, borderRadius: '50%', border: `3px solid ${aiSpeaking ? '#1976d2' : '#16a36a'}`, opacity: 0.3, animation: 'livePulse 1.5s ease-out infinite', '@keyframes livePulse': { '0%': { transform: 'scale(.92)', opacity: .5 }, '100%': { transform: 'scale(1.16)', opacity: 0 } } }} />
            )}
            <Box component="img" src={scenario.image} alt={displayName} sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover', boxShadow: '0 10px 35px rgba(15,23,42,.18)', filter: paused ? 'grayscale(.8)' : 'none' }} />
          </Box>
          <Typography variant="h6" fontWeight={800} sx={{ mt: 1.5 }}>{isEnglish ? `${displayName}, ${scenario.age}` : `${scenario.name}，${scenario.age} 歲`}</Typography>
          <Typography variant="caption" color={aiSpeaking ? 'primary.main' : listening ? 'success.main' : 'text.secondary'}>
            {aiSpeaking ? tr('對方說話中', 'Customer speaking') : listening ? tr('正在聆聽你', 'Listening to you') : typing ? tr('思考中', 'Thinking') : tr('通話中', 'On the call')}
          </Typography>
          <Waveform active={aiSpeaking || listening} color={aiSpeaking ? '#1976d2' : '#16a36a'} />
        </Box>

        <Box sx={{ width: '100%', maxWidth: 720, flex: 1, minHeight: 180, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 2, py: 2 }}>
          {(hint || hintLoading) && (
            <Paper sx={{ alignSelf: 'stretch', p: 2, borderRadius: 3, bgcolor: '#eaf2ff', border: '1px solid #cfe1ff' }} elevation={0}>
              <Typography variant="caption" color="primary" fontWeight={800}>💡 {tr('你可以這樣說', 'You could say')}</Typography>
              {hintLoading ? <CircularProgress size={18} sx={{ display: 'block', mt: 1 }} /> : <Typography sx={{ mt: .5, fontWeight: 700 }}>{hint}</Typography>}
            </Paper>
          )}
          {lastCustomer && (
            <Typography variant="h5" align="center" fontWeight={750} color="#153b6b" sx={{ lineHeight: 1.55 }}>
              {lastCustomer.text}
            </Typography>
          )}
          {partial && (
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="overline" color="text.secondary" fontWeight={800}>{tr('你說', 'You said')}</Typography>
              <Typography variant="h6" color="primary">{partial}<Box component="span" sx={{ display: 'inline-block', ml: .5, width: 2, height: 20, bgcolor: '#16a36a', animation: 'blink 1s step-end infinite', '@keyframes blink': { '50%': { opacity: 0 } } }} /></Typography>
            </Box>
          )}
          {error && <Alert severity="warning" onClose={() => setError('')} sx={{ alignSelf: 'stretch' }}>{error}</Alert>}
        </Box>

        <Box sx={{ flex: 'none', textAlign: 'center', pb: 1.5 }}>
          <Typography fontWeight={700}>{statusTitle}</Typography>
          <Typography variant="caption" color="text.secondary">
            {listening ? tr('說完後系統會自動送出', 'Your line is sent automatically when you finish') : aiSpeaking ? tr('直接開口即可打斷對方', 'Just start talking to interrupt') : typing ? tr('正在準備客戶回應', "Preparing the customer's reply") : tr('你可以查看任務或逐字稿', 'You can check the tasks or the transcript')}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ flex: 'none', px: 2, py: 2, bgcolor: 'rgba(255,255,255,.92)', borderTop: '1px solid rgba(15,23,42,.08)', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: { xs: 1.5, md: 3 } }}>
        <CallControl label={`${tr('任務', 'Tasks')} ${completedTasks.size}/${scenario.tasks.length}`} onClick={() => setShowTasks(true)}><AssignmentIcon /></CallControl>
        <CallControl label={tr('提示', 'Hint')} onClick={fetchHint} disabled={hintLoading}><LightbulbIcon /></CallControl>
        {micMode === 'hold' ? (
          <Box
            onPointerDown={holdStart}
            onPointerUp={holdEnd}
            onPointerLeave={holdEnd}
            sx={{ touchAction: 'none' }}
          >
            <CallControl label={holding ? tr('放開送出', 'Release to send') : tr('按住說話', 'Hold to talk')} primary disabled={paused || status !== 'connected'}>
              <MicIcon />
            </CallControl>
          </Box>
        ) : (
          <CallControl label={paused ? tr('解除靜音', 'Unmute') : tr('麥克風開啟', 'Mic on')} primary onClick={togglePause} disabled={status === 'connecting'}>
            {paused ? <MicOffIcon /> : <MicIcon />}
          </CallControl>
        )}
        <CallControl label={tr('逐字稿', 'Transcript')} onClick={() => setShowTranscript(true)}><TranscriptIcon /></CallControl>
        <Tooltip title={micMode === 'open' ? tr('切換成按住說話', 'Switch to hold-to-talk') : tr('切換成開放麥克風', 'Switch to open mic')}>
          <Box><CallControl label={micMode === 'open' ? tr('按住模式', 'Hold mode') : tr('開放模式', 'Open mode')} onClick={toggleMicMode}><GraphicEqIcon /></CallControl></Box>
        </Tooltip>
        <CallControl label={tr('結束', 'End')} danger onClick={finishPractice}><CallEndIcon /></CallControl>
      </Box>

      <Dialog open={showTasks} onClose={() => setShowTasks(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {tr('本次任務', 'Tasks for this call')}
          <IconButton onClick={() => setShowTasks(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          {scenario.tasks.map((task, index) => {
            const done = completedTasks.has(index);
            return (
              <Paper key={task.id} variant="outlined" sx={{ p: 2, mb: 1.5, borderRadius: 3, borderColor: done ? 'success.light' : 'divider', bgcolor: done ? 'rgba(22,163,106,.06)' : '#fff' }}>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  {done ? <CheckCircleIcon color="success" /> : <AssignmentIcon color="disabled" />}
                  <Box>
                    <Typography fontWeight={800}>{translateContent(task.title)}</Typography>
                    <Typography variant="body2" color="text.secondary">{translateContent(task.goal)}</Typography>
                  </Box>
                </Box>
              </Paper>
            );
          })}
        </DialogContent>
      </Dialog>

      <Dialog open={showTranscript} onClose={() => setShowTranscript(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {tr('通話逐字稿', 'Call transcript')}
          <IconButton onClick={() => setShowTranscript(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {messages.map((message, index) => (
            <Box key={message.id} sx={{ mb: 2 }}>
              {index > 0 && <Divider sx={{ mb: 2 }} />}
              <Typography variant="caption" color={message.role === 'advisor' ? 'primary' : 'text.secondary'} fontWeight={800}>
                {message.role === 'advisor' ? tr('你（專員）', 'You (advisor)') : displayName}
              </Typography>
              <Typography sx={{ mt: .5, lineHeight: 1.7 }}>{message.text}</Typography>
            </Box>
          ))}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export { PracticeSummary };
